import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { AdminCrewsTab, AdminInvitationsTab } from "@/pages/Crews";
import { AdminRatingsTab, AdminTimesheetsTab } from "@/pages/Phase6";
import { AdminInvoicesTab, AdminPayoutsTab } from "@/pages/Phase7";
import { AdminEscrowsTab } from "@/pages/Escrow";
import { AdminNotificationsTab } from "@/pages/AdminNotifications";
import { AdminVerificationsTab } from "@/pages/Verifications";
import { AdminTitleTaxonomyTab } from "@/pages/AdminTitleTaxonomy";
import { AdminMarketplacesTab } from "@/pages/AdminMarketplaces";
import { AdminWorkloadsTab } from "@/pages/AdminWorkloads";

const ADMIN_ROLES = [
  "client",
  "contractor",
  "lead_contractor",
  "helper",
  "event_agency",
  "super_admin",
];

const roleLabel = (role) => role?.replace(/_/g, " ") || "—";

export function AdminConsole() {
  const [tab, setTab] = useState("users");
  return (
    <Layout>
      <h1 className="text-3xl">Admin console</h1>
      <div data-testid={TID.adminDash} className="mt-6">
        <div className="flex flex-wrap gap-2 mb-6 border-b border-[var(--avcp-line)] pb-3">
          {[
            ["users", "Users"],
            ["fees", "Platform fees"],
            ["config", "Configuration"],
            ["audit", "Audit log"],
            ["crews", "Crews"],
            ["invitations", "Invitations"],
            ["ratings", "Ratings"],
            ["timesheets", "Timesheets"],
            ["invoices", "Invoices"],
            ["payouts", "Payouts"],
            ["escrows", "Escrows"],
            ["notifications", "Notifications"],
            ["verifications", "Verifications"],
            ["workloads", "Workloads"],
            ["resource_types", "Resource types"],
            ["marketplaces", "Marketplaces"],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`avcp-btn ${tab === k ? "primary" : "ghost"}`}>
              {l}
            </button>
          ))}
        </div>
        {tab === "users" && <AdminUsers />}
        {tab === "fees" && <AdminFees />}
        {tab === "config" && <AdminConfig />}
        {tab === "audit" && <AdminAudit />}
        {tab === "crews" && <AdminCrewsTab />}
        {tab === "invitations" && <AdminInvitationsTab />}
        {tab === "ratings" && <AdminRatingsTab />}
        {tab === "timesheets" && <AdminTimesheetsTab />}
        {tab === "invoices" && <AdminInvoicesTab />}
        {tab === "payouts" && <AdminPayoutsTab />}
        {tab === "escrows" && <AdminEscrowsTab />}
        {tab === "notifications" && <AdminNotificationsTab />}
        {tab === "verifications" && <AdminVerificationsTab />}
        {tab === "workloads" && <AdminWorkloadsTab />}
        {tab === "resource_types" && <AdminTitleTaxonomyTab />}
        {tab === "marketplaces" && <AdminMarketplacesTab />}
      </div>
    </Layout>
  );
}

export function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [roleSel, setRoleSel] = useState({});
  const [roleEditorId, setRoleEditorId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  async function reload() {
    const r = await api(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setUsers(r.data || []);
  }
  useEffect(() => { reload(); }, []); // eslint-disable-line

  async function act(uuid, path, body = null, successMessage = "Done") {
    setBusyId(uuid);
    try {
      await api(`/admin/users/${uuid}/${path}`, { method: "POST", body });
      await reload();
      toast.success(successMessage);
      return true;
    } catch (e) {
      toast.error(e?.problem?.detail || e?.problem?.title || e.message);
      return false;
    }
    finally { setBusyId(null); }
  }

  async function deleteUser(user) {
    setBusyId(user.uuid);
    try {
      await api(`/admin/users/${user.uuid}`, { method: "DELETE" });
      setDeleteConfirmId(null);
      setRoleEditorId(null);
      await reload();
      toast.success("Account deleted and anonymized.");
    } catch (e) {
      toast.error(e?.problem?.detail || e?.problem?.title || e.message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="avcp-card p-6">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input className="avcp-input max-w-sm" placeholder="Search by email or name" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="avcp-btn ghost" onClick={reload}>Search</button>
      </div>
      <div className="overflow-x-auto">
        <table data-testid={TID.adminUsersTable} className="avcp-table min-w-[900px]">
          <thead>
            <tr><th>User</th><th>Status</th><th>MFA</th><th>Verified</th><th>Role</th><th className="text-right">Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const currentRole = u.roles?.[0] || "client";
              const selectedRole = roleSel[u.uuid] || currentRole;
              const isCurrentAccount = currentUser?.uuid === u.uuid;
              return (
                <React.Fragment key={u.uuid}>
                  <tr data-testid={TID.adminUserRow}>
                    <td>
                      <div className="font-mono text-xs">{u.email}</div>
                      <div className="mt-1 text-xs text-[var(--avcp-ink-soft)]">{u.display_name || "No display name"}</div>
                    </td>
                    <td>
                      <span className={`avcp-pill ${u.status === "active" ? "success" : u.status === "suspended" ? "danger" : "warn"}`}>
                        {u.status}
                      </span>
                    </td>
                    <td aria-label={u.mfa_enabled ? "MFA enabled" : "MFA not enabled"}>{u.mfa_enabled ? "✓" : "—"}</td>
                    <td aria-label={u.email_verified_at ? "Email verified" : "Email not verified"}>{u.email_verified_at ? "✓" : "—"}</td>
                    <td><span className="avcp-pill ink text-xs">{roleLabel(currentRole)}</span></td>
                    <td>
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        <Link to={`/app/admin/users/${u.uuid}/snapshot`}
                          data-testid="admin-user-snapshot-link"
                          className="avcp-btn ghost text-xs !px-3 !py-2">
                          Snapshot
                        </Link>
                        {isCurrentAccount ? (
                          <span className="avcp-pill text-xs">Current account</span>
                        ) : (
                          <>
                            <button
                              className="avcp-btn ghost text-xs !px-3 !py-2"
                              aria-expanded={roleEditorId === u.uuid}
                              onClick={() => {
                                setDeleteConfirmId(null);
                                setRoleSel((current) => ({ ...current, [u.uuid]: currentRole }));
                                setRoleEditorId(roleEditorId === u.uuid ? null : u.uuid);
                              }}
                            >
                              Change role
                            </button>
                            {u.status === "suspended" ? (
                              <button data-testid={TID.adminRestore} className="avcp-btn ghost text-xs !px-3 !py-2" disabled={busyId === u.uuid}
                                onClick={() => act(u.uuid, "restore", null, "Account restored.")}>Restore</button>
                            ) : (
                              <button data-testid={TID.adminSuspend} className="avcp-btn ghost text-xs !px-3 !py-2" disabled={busyId === u.uuid}
                                onClick={() => act(u.uuid, "suspend", null, "Account suspended.")}>Suspend</button>
                            )}
                            <button className="avcp-btn danger text-xs !px-3 !py-2" disabled={busyId === u.uuid}
                              aria-expanded={deleteConfirmId === u.uuid}
                              onClick={() => {
                                setRoleEditorId(null);
                                setDeleteConfirmId(deleteConfirmId === u.uuid ? null : u.uuid);
                              }}>Delete</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {roleEditorId === u.uuid && !isCurrentAccount && (
                    <tr>
                      <td colSpan="6" className="bg-[var(--avcp-primary-soft)]/40">
                        <div className="flex flex-wrap items-end justify-between gap-3 py-1">
                          <label className="grid gap-1.5">
                            <span className="text-sm font-medium">Change role for {u.email}</span>
                            <select className="avcp-input text-sm !w-56"
                              aria-label={`New role for ${u.email}`}
                              disabled={busyId === u.uuid}
                              value={selectedRole}
                              onChange={(e) => setRoleSel({ ...roleSel, [u.uuid]: e.target.value })}>
                              {ADMIN_ROLES.map((role) => (
                                <option key={role} value={role}>{roleLabel(role)}</option>
                              ))}
                            </select>
                          </label>
                          <div className="flex items-center gap-2">
                            <button data-testid={TID.adminGrantRole} className="avcp-btn primary text-xs !px-4 !py-2"
                              disabled={busyId === u.uuid || selectedRole === currentRole}
                              onClick={async () => {
                                const changed = await act(
                                  u.uuid,
                                  "grant-role",
                                  { role_code: selectedRole },
                                  `Role changed to ${roleLabel(selectedRole)}.`
                                );
                                if (changed) setRoleEditorId(null);
                              }}>
                              {busyId === u.uuid ? "Saving…" : "Set role"}
                            </button>
                            <button className="avcp-btn ghost text-xs !px-4 !py-2" disabled={busyId === u.uuid}
                              onClick={() => setRoleEditorId(null)}>Cancel</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {deleteConfirmId === u.uuid && !isCurrentAccount && (
                    <tr>
                      <td colSpan="6" className="bg-red-50">
                        <div className="flex flex-wrap items-center justify-between gap-3 py-1">
                          <div>
                            <div className="text-sm font-semibold text-[var(--avcp-danger)]">Delete {u.email}?</div>
                            <p className="mt-1 text-xs text-[var(--avcp-ink-soft)]">
                              This deactivates and anonymizes the account while retaining required audit and financial records.
                            </p>
                          </div>
                          <div className="flex items-center gap-2" role="group" aria-label={`Confirm deletion of ${u.email}`}>
                            <button className="avcp-btn danger text-xs !px-4 !py-2" disabled={busyId === u.uuid}
                              onClick={() => deleteUser(u)}>
                              {busyId === u.uuid ? "Deleting…" : "Confirm delete"}
                            </button>
                            <button className="avcp-btn ghost text-xs !px-4 !py-2" disabled={busyId === u.uuid}
                              onClick={() => setDeleteConfirmId(null)}>Cancel</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminFees() {
  const [feesByKey, setFeesByKey] = useState({});
  const [history, setHistory] = useState({ "fees.client_per_hour": [], "fees.contractor_per_hour": [] });
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  async function reload() {
    const r = await api("/admin/config");
    const m = {};
    (r.data || []).forEach((row) => { m[row.key] = row; });
    setFeesByKey(m);
    setDrafts({
      "fees.client_per_hour": m["fees.client_per_hour"]?.value_json?.amount || "",
      "fees.contractor_per_hour": m["fees.contractor_per_hour"]?.value_json?.amount || "",
      currency: m["fees.client_per_hour"]?.value_json?.currency || "USD",
    });
    for (const k of ["fees.client_per_hour", "fees.contractor_per_hour"]) {
      try {
        const h = await api(`/admin/config/history?key=${encodeURIComponent(k)}`);
        setHistory((cur) => ({ ...cur, [k]: h.data || [] }));
      } catch {}
    }
  }
  useEffect(() => { reload(); }, []);

  async function save() {
    setBusy(true);
    try {
      const currency = drafts.currency || "USD";
      await api("/admin/config", { method: "PATCH",
        body: { key: "fees.client_per_hour", value: { amount: String(drafts["fees.client_per_hour"]), currency } } });
      await api("/admin/config", { method: "PATCH",
        body: { key: "fees.contractor_per_hour", value: { amount: String(drafts["fees.contractor_per_hour"]), currency } } });
      toast.success("Fees updated");
      await reload();
    } catch (e) { toast.error(e?.problem?.detail || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="avcp-card p-6">
        <h2 className="text-lg">Platform fees</h2>
        <p className="mt-1 text-sm text-[var(--avcp-ink-soft)]">
          Configurable per AR-4. Snapshots are taken on every booking acceptance.
        </p>
        <div className="mt-4 grid gap-3">
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">Client fee (per hour)</span>
            <input data-testid={TID.adminFeesClient} className="avcp-input font-mono" value={drafts["fees.client_per_hour"] || ""}
              onChange={(e) => setDrafts({ ...drafts, "fees.client_per_hour": e.target.value })} />
          </label>
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">Contractor fee (per hour)</span>
            <input data-testid={TID.adminFeesContractor} className="avcp-input font-mono" value={drafts["fees.contractor_per_hour"] || ""}
              onChange={(e) => setDrafts({ ...drafts, "fees.contractor_per_hour": e.target.value })} />
          </label>
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">Currency</span>
            <select className="avcp-input" value={drafts.currency || "USD"}
              onChange={(e) => setDrafts({ ...drafts, currency: e.target.value })}>
              {["CAD", "USD", "EUR", "GBP"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <button data-testid={TID.adminFeesSave} className="avcp-btn primary" disabled={busy} onClick={save}>
            {busy ? "Saving…" : "Save fees"}
          </button>
        </div>
      </div>
      <div className="avcp-card p-6">
        <h2 className="text-lg">Fee change history</h2>
        {["fees.client_per_hour", "fees.contractor_per_hour"].map((k) => (
          <div key={k} className="mt-4">
            <div className="text-sm font-semibold">{k}</div>
            <ul className="mt-2 grid gap-1 text-xs font-mono">
              {(history[k] || []).slice(0, 6).map((h) => (
                <li key={`${k}-${h.version}`} className="flex justify-between border-b border-[var(--avcp-line)] py-1">
                  <span>v{h.version} · {h.value_json?.amount} {h.value_json?.currency}</span>
                  <span className="text-[var(--avcp-ink-soft)]">{h.updated_at?.slice(0, 19)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminConfig() {
  const [config, setConfig] = useState([]);
  useEffect(() => { (async () => setConfig((await api("/admin/config")).data || []))(); }, []);
  return (
    <div className="avcp-card p-6">
      <h2 className="text-lg">Platform configuration</h2>
      <p className="mt-1 text-sm text-[var(--avcp-ink-soft)]">Read-only view; edits land per key via the Fees panel and (Phase 3+) feature flag console.</p>
      <table className="avcp-table mt-4">
        <thead><tr><th>Key</th><th>Value</th><th>Version</th><th>Updated</th></tr></thead>
        <tbody>
          {config.map((r) => (
            <tr key={r.key} data-testid={TID.adminConfigRow}>
              <td className="font-mono text-xs">{r.key}</td>
              <td><pre className="text-xs font-mono whitespace-pre-wrap">{JSON.stringify(r.value_json)}</pre></td>
              <td>{r.version}</td>
              <td className="text-xs">{r.updated_at?.slice(0, 19)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdminAudit() {
  const [events, setEvents] = useState([]);
  const [eventType, setEventType] = useState("");
  async function load() {
    const r = await api(`/admin/audit${eventType ? `?event_type=${encodeURIComponent(eventType)}` : ""}`);
    setEvents(r.data || []);
  }
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="avcp-card p-6">
      <h2 className="text-lg">Audit log</h2>
      <div className="mt-3 flex gap-2">
        <input className="avcp-input max-w-sm font-mono" placeholder="event_type filter" value={eventType} onChange={(e) => setEventType(e.target.value)} />
        <button className="avcp-btn ghost" onClick={load}>Refresh</button>
      </div>
      <table data-testid={TID.adminAuditTable} className="avcp-table mt-4">
        <thead><tr><th>When</th><th>Actor</th><th>Event</th><th>Resource</th><th>Data</th></tr></thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id}>
              <td className="text-xs font-mono">{e.occurred_at?.slice(0, 19)}</td>
              <td className="text-xs">{e.actor_kind}{e.actor_user_id ? `#${e.actor_user_id}` : ""}</td>
              <td className="font-mono text-xs">{e.event_type}</td>
              <td className="text-xs">{e.resource_kind}{e.resource_id ? `#${e.resource_id}` : ""}</td>
              <td><pre className="text-xs font-mono whitespace-pre-wrap max-w-md">{JSON.stringify(e.data_json || {})}</pre></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
